import { ICompany } from "./company";

export interface ICropTypeChemicalField {
  fieldName: string;
  group?: string;
  aliases?: string[];
  unit?: string;
}

export interface ICropType {
  _id: string;
  name: string;
  description?: string;
  isPublic?: boolean;
  companies?: ICompany[];
  availablePredictionTargets?: string[];
  aliases?: string[];
  chemicalFields?: ICropTypeChemicalField[];
}

/** Case-insensitive, space-insensitive key for crop type name/alias comparison. */
export function normalizeCropTypeKey(value?: string | null): string {
  return (value || "").trim().toLowerCase().replace(/\s+/g, "");
}

export function getCropTypeKeys(
  cropType: Pick<ICropType, "name" | "aliases">
): string[] {
  return [cropType.name, ...(cropType.aliases || [])]
    .map((v) => normalizeCropTypeKey(v))
    .filter(Boolean);
}

export function findCropTypeByNameOrAlias<
  T extends Pick<ICropType, "name" | "aliases">
>(value: string | undefined | null, cropTypes: T[]): T | undefined {
  const key = normalizeCropTypeKey(value);
  if (!key) return undefined;
  return cropTypes.find((ct) => getCropTypeKeys(ct).includes(key));
}

export function resolveCropTypeName(
  value: string | undefined | null,
  cropTypes: Pick<ICropType, "name" | "aliases">[]
): string | undefined {
  if (!value) return undefined;
  const match = findCropTypeByNameOrAlias(value, cropTypes);
  return match?.name || value;
}

export function cropTypesMatch(
  a?: string | null,
  b?: string | null,
  cropTypes?: Pick<ICropType, "name" | "aliases">[]
): boolean {
  const keyA = normalizeCropTypeKey(a);
  const keyB = normalizeCropTypeKey(b);
  if (!keyA || !keyB) return false;
  if (keyA === keyB) return true;
  if (!cropTypes?.length) return false;
  const resolvedA = normalizeCropTypeKey(resolveCropTypeName(a, cropTypes));
  const resolvedB = normalizeCropTypeKey(resolveCropTypeName(b, cropTypes));
  return !!resolvedA && resolvedA === resolvedB;
}

/** Whether sampleType matches a canonical crop type name (e.g. "CS"), including aliases. */
export function isCropTypeNamed(
  sampleType: string | undefined | null,
  targetName: string,
  cropTypes?: Pick<ICropType, "name" | "aliases">[]
): boolean {
  return cropTypesMatch(sampleType, targetName, cropTypes);
}
